import Sidebar from '@/components/layout/sidebar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { isUnauthorizedError } from '@/lib/authUtils';
import { generateProjectTasksPDF, generateProjectsPDF } from '@/lib/pdfUtils';
import { apiRequest } from '@/lib/queryClient';
import { zodResolver } from '@hookform/resolvers/zod';
import type { Project, TaskWithRelations, Team } from '@shared/schema';
import { insertProjectSchema } from '@shared/schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  Download,
  FolderOpen,
  Plus,
  Settings,
  Target,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const projectFormSchema = insertProjectSchema.extend({
  color: z.string().regex(/^#[0-9A-F]{6}$/i, 'Please enter a valid hex color'),
  hasAmc: z.boolean().optional(),
  amcExpiryDate: z.string().optional(),
  supportPackageValidityPeriod: z.string().optional(),
  teamId: z.string().optional(),
});

type ProjectFormData = z.infer<typeof projectFormSchema>;

export default function Projects() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] =
    useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isPendingTasksModalOpen, setIsPendingTasksModalOpen] = useState(false);
  const [selectedProjectForPendingTasks, setSelectedProjectForPendingTasks] =
    useState<Project | null>(null);
  const [selectedTaskFilter, setSelectedTaskFilter] = useState<
    'all' | 'completed' | 'notCompleted' | 'inProgress'
  >('all');
  const [projectFilter, setProjectFilter] = useState<'all' | 'inprogress'>(
    'all'
  );
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingProjectsPDF, setIsGeneratingProjectsPDF] = useState(false);

  const editForm = useForm<ProjectFormData>({
    resolver: zodResolver(projectFormSchema),
    defaultValues: {
      name: '',
      description: '',
      color: '#3B82F6',
      hasAmc: false,
      amcExpiryDate: '',
      supportPackageValidityPeriod: '',
      teamId: '',
    },
  });
  useEffect(() => {
    if (selectedProject) {
      editForm.reset({
        name: selectedProject.name || '',
        description: selectedProject.description || '',
        color: selectedProject.color || '#3B82F6',
        hasAmc: (selectedProject as any).hasAmc || false,
        amcExpiryDate: selectedProject.amcExpiryDate
          ? new Date(selectedProject.amcExpiryDate).toISOString().slice(0, 10)
          : '',
        supportPackageValidityPeriod:
          selectedProject.supportPackageValidityPeriod
            ? new Date(selectedProject.supportPackageValidityPeriod)
                .toISOString()
                .slice(0, 10)
            : '',
        teamId: (selectedProject as any).teamId || '',
      });
    }
  }, [selectedProject]);

  // Form setup
  const form = useForm<ProjectFormData>({
    resolver: zodResolver(projectFormSchema),
    defaultValues: {
      name: '',
      description: '',
      color: '#3B82F6',
      hasAmc: false,
      amcExpiryDate: '',
      supportPackageValidityPeriod: '',
      teamId: '',
    },
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      toast({
        title: 'Unauthorized',
        description: 'You are logged out. Logging in again...',
        variant: 'destructive',
      });
      setTimeout(() => {
        window.location.href = '/api/login';
      }, 500);
    }
  }, [user, authLoading, toast]);

  // Fetch data
  const { data: projects = [], isLoading: projectsLoading } = useQuery<
    Project[]
  >({
    queryKey: ['/api/projects'],
    retry: false,
  });

  const { data: allTasks = [] } = useQuery<TaskWithRelations[]>({
    queryKey: ['/api/tasks'],
    retry: false,
  });

  const { data: teams = [] } = useQuery<Team[]>({
    queryKey: ['/api/teams'],
    retry: false,
  });

  // Compute visible projects safely when data or filter changes
  const visibleProjects = useMemo(() => {
    const list = projects || [];
    if (projectFilter === 'all') return list;
    return list.filter(p => {
      const stats = getProjectStats(p.id);
      return (stats?.inProgress || 0) > 0;
    });
  }, [projects, projectFilter, allTasks]);

  // Create project mutation
  const createProjectMutation = useMutation({
    mutationFn: async (data: ProjectFormData) => {
      await apiRequest('POST', '/api/projects', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/projects'] });
      setIsCreateProjectModalOpen(false);
      form.reset();
      toast({
        title: 'Success',
        description: 'Project created successfully',
      });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to create project',
        variant: 'destructive',
      });
    },
  });

  // Edit project mutation
  const updateProjectMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ProjectFormData }) => {
      const res = await apiRequest('PATCH', `/api/projects/${id}`, data);
      return res as any; // updated project from server
    },
    onSuccess: (updated: any) => {
      const id = updated.id as string;
      // Keep the modal open and reflect changes immediately from server response
      setSelectedProject(prev =>
        prev && prev.id === id ? ({ ...prev, ...updated } as any) : prev
      );
      // Reset the form to the saved values so fields show the new team/color/etc.
      editForm.reset({
        name: updated.name || '',
        description: (updated as any).description || '',
        color: updated.color || '#3B82F6',
        hasAmc: (updated as any).hasAmc || false,
        amcExpiryDate: updated.amcExpiryDate
          ? new Date(updated.amcExpiryDate).toISOString().slice(0, 10)
          : '',
        supportPackageValidityPeriod: updated.supportPackageValidityPeriod
          ? new Date(updated.supportPackageValidityPeriod)
              .toISOString()
              .slice(0, 10)
          : '',
        teamId: (updated as any).teamId || '',
      });
      // Refresh projects list in background
      queryClient.invalidateQueries({ queryKey: ['/api/projects'] });
      toast({ title: 'Success', description: 'Project updated' });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to update project',
        variant: 'destructive',
      });
    },
  });

  if (authLoading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading projects...</p>
          </div>
        </div>
      </div>
    );
  }

  const canManage = user?.role === 'manager' || user?.role === 'admin';

  const handleCreateProject = (data: ProjectFormData) => {
    createProjectMutation.mutate(data);
  };

  function getProjectStats(projectId: string) {
    const projectTasks = allTasks.filter(task => task.projectId === projectId);
    const totalTasks = projectTasks.length;
    const completedTasks = projectTasks.filter(
      task => task.status === 'complete' && task.approvedAt
    ).length;
    const inProgressTasks = projectTasks.filter(task =>
      ['started', 'working'].includes(task.status)
    ).length;
    const pendingTasks = projectTasks.filter(
      task => task.status === 'pending'
    ).length;
    const notCompletedTasks = projectTasks.filter(
      task => !['complete', 'pending_approval', 'dropped'].includes(task.status)
    ).length;

    return {
      total: totalTasks,
      completed: completedTasks,
      inProgress: inProgressTasks,
      pending: pendingTasks,
      notCompleted: notCompletedTasks,
      completionRate:
        totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0,
    };
  }

  const popularColors = [
    '#3B82F6',
    '#EF4444',
    '#10B981',
    '#F59E0B',
    '#8B5CF6',
    '#06B6D4',
    '#84CC16',
    '#F97316',
    '#EC4899',
    '#6B7280',
  ];

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      <Sidebar />

      <div className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1
                  className="text-3xl font-bold text-gray-900 dark:text-gray-100"
                  data-testid="text-page-title"
                >
                  Projects
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-2">
                  Organize tasks into projects and track progress
                </p>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant={projectFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setProjectFilter('all')}
                  data-testid="button-project-filter-all"
                >
                  All
                </Button>
                <Button
                  variant={
                    projectFilter === 'inprogress' ? 'default' : 'outline'
                  }
                  size="sm"
                  onClick={() => setProjectFilter('inprogress')}
                  data-testid="button-project-filter-inprogress"
                >
                  In Progress
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    const projectsToExport = visibleProjects.map(project => {
                      const stats = getProjectStats(project.id);
                      return {
                        id: project.id,
                        name: project.name || '',
                        description: project.description,
                        color: project.color,
                        createdAt: project.createdAt,
                        totalTasks: stats.total,
                        completedTasks: stats.completed,
                        inProgressTasks: stats.inProgress,
                        pendingTasks: stats.pending,
                        notCompletedTasks: stats.notCompleted,
                        completionRate: stats.completionRate,
                      };
                    });

                    if (projectsToExport.length === 0) {
                      toast({
                        title: 'No Projects',
                        description: 'No projects found to generate PDF',
                        variant: 'destructive',
                      });
                      return;
                    }

                    setIsGeneratingProjectsPDF(true);
                    try {
                      const filterType =
                        projectFilter === 'all'
                          ? 'All Projects'
                          : 'In Progress Projects';
                      await generateProjectsPDF(projectsToExport, {
                        filterType: filterType,
                      });

                      toast({
                        title: 'Success',
                        description: 'Projects PDF downloaded successfully',
                      });
                    } catch (error) {
                      console.error('Error generating projects PDF:', error);
                      toast({
                        title: 'Error',
                        description:
                          'Failed to generate PDF. Please try again.',
                        variant: 'destructive',
                      });
                    } finally {
                      setIsGeneratingProjectsPDF(false);
                    }
                  }}
                  disabled={
                    isGeneratingProjectsPDF || visibleProjects.length === 0
                  }
                  className="flex items-center gap-2"
                >
                  {isGeneratingProjectsPDF ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Download Projects
                    </>
                  )}
                </Button>
              </div>

              {canManage && (
                <Dialog
                  open={isCreateProjectModalOpen}
                  onOpenChange={setIsCreateProjectModalOpen}
                >
                  <DialogTrigger asChild>
                    <Button data-testid="button-create-project">
                      <Plus className="w-4 h-4 mr-2" />
                      New Project
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New Project</DialogTitle>
                      <DialogDescription>
                        Set up a new project to organize related tasks
                      </DialogDescription>
                    </DialogHeader>

                    <form
                      onSubmit={form.handleSubmit(handleCreateProject)}
                      className="space-y-4"
                    >
                      <div>
                        <Label htmlFor="project-name">Project Name</Label>
                        <Input
                          id="project-name"
                          {...form.register('name')}
                          placeholder="Enter project name"
                          data-testid="input-project-name"
                        />
                        {form.formState.errors.name && (
                          <p className="text-sm text-red-600 mt-1">
                            {form.formState.errors.name.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="project-description">Description</Label>
                        <Textarea
                          id="project-description"
                          {...form.register('description')}
                          placeholder="Project description (optional)"
                          rows={3}
                          data-testid="textarea-project-description"
                        />
                        {form.formState.errors.description && (
                          <p className="text-sm text-red-600 mt-1">
                            {form.formState.errors.description.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="project-color">Project Color</Label>
                        <div className="mt-2">
                          <Input
                            id="project-color"
                            type="color"
                            {...form.register('color')}
                            className="w-20 h-10"
                            data-testid="input-project-color"
                          />
                          <div className="flex flex-wrap gap-2 mt-2">
                            {popularColors.map(color => (
                              <button
                                key={color}
                                type="button"
                                className="w-6 h-6 rounded border-2 border-gray-300 hover:border-gray-500"
                                style={{ backgroundColor: color }}
                                onClick={() => form.setValue('color', color)}
                                data-testid={`color-${color.replace('#', '')}`}
                              />
                            ))}
                          </div>
                        </div>
                        {form.formState.errors.color && (
                          <p className="text-sm text-red-600 mt-1">
                            {form.formState.errors.color.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="team">Team (optional)</Label>
                        <select
                          id="team"
                          className="mt-1 border rounded px-3 py-2 w-full bg-white text-gray-900"
                          {...form.register('teamId')}
                        >
                          <option value="">No team</option>
                          {teams
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map(t => (
                              <option key={t.id} value={t.id}>
                                {t.name}
                              </option>
                            ))}
                        </select>
                      </div>

                      <div>
                        <div className="flex items-center space-x-2">
                          <Input
                            id="has-amc"
                            type="checkbox"
                            {...form.register('hasAmc')}
                            className="w-4 h-4"
                            data-testid="checkbox-has-amc"
                          />
                          <Label htmlFor="has-amc">
                            This project has AMC (Annual Maintenance Contract)
                          </Label>
                        </div>
                      </div>

                      {form.watch('hasAmc') && (
                        <div>
                          <Label htmlFor="amc-expiry">AMC Expiry Date</Label>
                          <Input
                            id="amc-expiry"
                            type="date"
                            {...form.register('amcExpiryDate')}
                            className="mt-1"
                            data-testid="input-amc-expiry-date"
                          />
                        </div>
                      )}

                      <div>
                        <Label htmlFor="support-validity">
                          Support Package Validity Period
                        </Label>
                        <Input
                          id="support-validity"
                          type="date"
                          {...form.register('supportPackageValidityPeriod')}
                          className="mt-1"
                          data-testid="input-support-package-validity"
                        />
                      </div>

                      <DialogFooter>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setIsCreateProjectModalOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          disabled={createProjectMutation.isPending}
                          data-testid="button-submit-project"
                        >
                          {createProjectMutation.isPending
                            ? 'Creating...'
                            : 'Create Project'}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>

          {/* Projects Grid */}
          {projectsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-500">Loading projects...</p>
            </div>
          ) : projects.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <FolderOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  No projects yet
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  Create your first project to start organizing tasks
                </p>
                {canManage && (
                  <Button onClick={() => setIsCreateProjectModalOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Project
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visibleProjects.length === 0 ? (
                <Card className="md:col-span-2 lg:col-span-3">
                  <CardContent className="text-center py-12">
                    <FolderOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                      {projectFilter === 'all'
                        ? 'No projects yet'
                        : 'No in-progress projects'}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                      {projectFilter === 'all'
                        ? 'Create your first project to start organizing tasks'
                        : 'Switch to All to view every project'}
                    </p>
                    {canManage && projectFilter === 'all' && (
                      <Button onClick={() => setIsCreateProjectModalOpen(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Create Project
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (
                visibleProjects.map(project => {
                  const stats = getProjectStats(project.id);

                  return (
                    <Card
                      key={project.id}
                      className="cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={() => setSelectedProject(project)}
                      data-testid={`project-card-${project.id}`}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center space-x-3">
                            <div
                              className="w-4 h-4 rounded-full"
                              style={{
                                backgroundColor: project.color || '#3B82F6',
                              }}
                            />
                            <div>
                              <CardTitle className="text-lg">
                                {project.name}
                              </CardTitle>
                              {project.description && (
                                <CardDescription className="mt-1">
                                  {project.description}
                                </CardDescription>
                              )}
                            </div>
                          </div>
                          {canManage && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setSelectedProject(project)}
                            >
                              <Settings className="w-4 h-4 mr-1" /> Edit
                            </Button>
                          )}
                        </div>
                      </CardHeader>

                      <CardContent>
                        {/* Project Stats */}
                        <div className="grid grid-cols-4 gap-2 mb-4">
                          <div
                            className="text-center p-2 bg-gray-50 dark:bg-gray-800 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            onClick={e => {
                              e.stopPropagation();
                              setSelectedProjectForPendingTasks(project);
                              setSelectedTaskFilter('all');
                              setIsPendingTasksModalOpen(true);
                            }}
                            title="Click to view all tasks"
                          >
                            <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                              {stats.total}
                            </div>
                            <div className="text-xs text-gray-500">
                              Total Tasks
                            </div>
                          </div>
                          <div
                            className="text-center p-2 bg-green-50 dark:bg-green-900/20 rounded cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                            onClick={e => {
                              e.stopPropagation();
                              setSelectedProjectForPendingTasks(project);
                              setSelectedTaskFilter('completed');
                              setIsPendingTasksModalOpen(true);
                            }}
                            title="Click to view completed tasks"
                          >
                            <div className="text-lg font-bold text-green-600 dark:text-green-400">
                              {stats.completed}
                            </div>
                            <div className="text-xs text-green-600 dark:text-green-400">
                              Completed
                            </div>
                          </div>
                          <div
                            className="text-center p-2 bg-orange-50 dark:bg-orange-900/20 rounded cursor-pointer hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors"
                            onClick={e => {
                              e.stopPropagation();
                              setSelectedProjectForPendingTasks(project);
                              setSelectedTaskFilter('inProgress');
                              setIsPendingTasksModalOpen(true);
                            }}
                            title="Click to view in progress tasks"
                          >
                            <div className="text-lg font-bold text-orange-600 dark:text-orange-400">
                              {stats.inProgress}
                            </div>
                            <div className="text-xs text-orange-600 dark:text-orange-400">
                              In Progress
                            </div>
                          </div>
                          <div
                            className="text-center p-2 bg-red-50 dark:bg-red-900/20 rounded cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                            onClick={e => {
                              e.stopPropagation();
                              setSelectedProjectForPendingTasks(project);
                              setSelectedTaskFilter('notCompleted');
                              setIsPendingTasksModalOpen(true);
                            }}
                            title="Click to view pending tasks"
                          >
                            <div className="text-lg font-bold text-red-600 dark:text-red-400">
                              {stats.notCompleted}
                            </div>
                            <div className="text-xs text-red-600 dark:text-red-400">
                              Not Completed
                            </div>
                          </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="mb-3">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600 dark:text-gray-400">
                              Progress
                            </span>
                            <span className="text-gray-900 dark:text-gray-100 font-medium">
                              {stats.completionRate}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className="h-2 rounded-full transition-all duration-300"
                              style={{
                                width: `${stats.completionRate}%`,
                                backgroundColor: project.color || '#3B82F6',
                              }}
                            />
                          </div>
                        </div>

                        {/* Status Badges */}
                        <div className="flex flex-wrap gap-1">
                          {stats.inProgress > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {stats.inProgress} In Progress
                            </Badge>
                          )}
                          {stats.pending > 0 && (
                            <Badge variant="outline" className="text-xs">
                              {stats.pending} Pending
                            </Badge>
                          )}
                        </div>

                        <div className="mt-3 space-y-1">
                          <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                            <Calendar className="w-3 h-3 mr-1" />
                            Created{' '}
                            {new Date(project.createdAt!).toLocaleDateString()}
                          </div>
                          {project.hasAmc && project.amcExpiryDate && (
                            <div className="flex items-center text-xs text-orange-600 dark:text-orange-400">
                              <Target className="w-3 h-3 mr-1" />
                              AMC expires:{' '}
                              {new Date(
                                project.amcExpiryDate
                              ).toLocaleDateString()}
                            </div>
                          )}
                          {project.supportPackageValidityPeriod && (
                            <div className="flex items-center text-xs text-blue-600 dark:text-blue-400">
                              <Target className="w-3 h-3 mr-1" />
                              Support expires:{' '}
                              {new Date(
                                project.supportPackageValidityPeriod
                              ).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          )}

          {/* Project Details Modal */}
          {selectedProject && (
            <Dialog
              open={!!selectedProject}
              onOpenChange={() => setSelectedProject(null)}
            >
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="flex items-center space-x-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{
                        backgroundColor: selectedProject.color || '#3B82F6',
                      }}
                    />
                    <span>{selectedProject.name}</span>
                  </DialogTitle>
                  {selectedProject.description && (
                    <DialogDescription>
                      {selectedProject.description}
                    </DialogDescription>
                  )}
                  {selectedProject.hasAmc && selectedProject.amcExpiryDate && (
                    <div className="mt-2 text-sm text-orange-600 dark:text-orange-400 flex items-center">
                      <Target className="w-4 h-4 mr-2" />
                      AMC expires on{' '}
                      {new Date(
                        selectedProject.amcExpiryDate
                      ).toLocaleDateString()}
                    </div>
                  )}
                  {selectedProject.supportPackageValidityPeriod && (
                    <div className="mt-2 text-sm text-blue-600 dark:text-blue-400 flex items-center">
                      <Target className="w-4 h-4 mr-2" />
                      Support package expires on{' '}
                      {new Date(
                        selectedProject.supportPackageValidityPeriod
                      ).toLocaleDateString()}
                    </div>
                  )}
                </DialogHeader>

                <div className="space-y-4">
                  {/* Detailed Stats */}
                  <div className="grid grid-cols-4 gap-4">
                    {Object.entries(getProjectStats(selectedProject.id)).map(
                      ([key, value]) => (
                        <div
                          key={key}
                          className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                        >
                          <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                            {typeof value === 'number' ? value : `${value}%`}
                          </div>
                          <div className="text-xs text-gray-500 capitalize">
                            {key === 'completionRate'
                              ? 'Completion'
                              : key.replace(/([A-Z])/g, ' $1')}
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  {/* Project Tasks Summary */}
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Recent Tasks
                    </h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {allTasks
                        .filter(task => task.projectId === selectedProject.id)
                        .slice(0, 5)
                        .map(task => (
                          <div
                            key={task.id}
                            className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded"
                          >
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {task.title}
                            </span>
                            <Badge
                              variant={
                                task.status === 'complete'
                                  ? 'default'
                                  : 'secondary'
                              }
                              className="text-xs"
                            >
                              {task.status}
                            </Badge>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>

                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setSelectedProject(null)}
                  >
                    Close
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Edit Project Modal */}
      <Dialog
        open={!!selectedProject}
        onOpenChange={open => {
          if (!open) setSelectedProject(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
            <DialogDescription>Update project details</DialogDescription>
          </DialogHeader>
          {selectedProject && (
            <form
              onSubmit={editForm.handleSubmit(data =>
                updateProjectMutation.mutate({ id: selectedProject.id, data })
              )}
              className="space-y-4"
            >
              <div>
                <Label>Name</Label>
                <Input {...editForm.register('name')} />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea rows={3} {...editForm.register('description')} />
              </div>
              <div>
                <Label>Color</Label>
                <Input
                  type="color"
                  className="w-20 h-10"
                  {...editForm.register('color')}
                />
              </div>
              <div>
                <Label>Team (optional)</Label>
                <select
                  className="mt-1 border rounded px-3 py-2 w-full bg-white text-gray-900"
                  {...editForm.register('teamId')}
                >
                  <option value="">No team</option>
                  {teams
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(t => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  id="edit-has-amc"
                  type="checkbox"
                  className="w-4 h-4"
                  {...editForm.register('hasAmc')}
                />
                <Label htmlFor="edit-has-amc">This project has AMC</Label>
              </div>
              {editForm.watch('hasAmc') && (
                <div>
                  <Label>AMC Expiry Date</Label>
                  <Input type="date" {...editForm.register('amcExpiryDate')} />
                </div>
              )}
              <div>
                <Label>Support Package Validity Period</Label>
                <Input
                  type="date"
                  {...editForm.register('supportPackageValidityPeriod')}
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSelectedProject(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updateProjectMutation.isPending}
                >
                  {updateProjectMutation.isPending
                    ? 'Saving...'
                    : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Tasks Modal */}
      <Dialog
        open={isPendingTasksModalOpen}
        onOpenChange={setIsPendingTasksModalOpen}
      >
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>
                  {selectedTaskFilter === 'all' && 'All Tasks'}
                  {selectedTaskFilter === 'completed' && 'Completed Tasks'}
                  {selectedTaskFilter === 'notCompleted' && 'Pending Tasks'}
                  {selectedTaskFilter === 'inProgress' && 'In Progress Tasks'}
                  {' - '}
                  {selectedProjectForPendingTasks?.name}
                </DialogTitle>
                <DialogDescription>
                  {selectedTaskFilter === 'all' && 'All tasks in this project'}
                  {selectedTaskFilter === 'completed' &&
                    'Tasks that are completed in this project'}
                  {selectedTaskFilter === 'notCompleted' &&
                    'Tasks that are not completed in this project'}
                  {selectedTaskFilter === 'inProgress' &&
                    'Tasks that are currently in progress'}
                </DialogDescription>
              </div>
              <Button
                variant="outline"
                onClick={async () => {
                  if (!selectedProjectForPendingTasks) return;

                  // Get filtered tasks
                  let filteredTasks = allTasks.filter(
                    task => task.projectId === selectedProjectForPendingTasks.id
                  );

                  // Filter based on selectedTaskFilter
                  if (selectedTaskFilter === 'completed') {
                    filteredTasks = filteredTasks.filter(
                      task => task.status === 'complete' && task.approvedAt
                    );
                  } else if (selectedTaskFilter === 'inProgress') {
                    // For "in progress" tasks, filter by status 'started' or 'working'
                    filteredTasks = filteredTasks.filter(task =>
                      ['started', 'working'].includes(task.status)
                    );
                  } else if (selectedTaskFilter === 'notCompleted') {
                    // For "not completed" tasks, exclude completed, pending approval, and dropped
                    filteredTasks = filteredTasks.filter(
                      task =>
                        !['complete', 'pending_approval', 'dropped'].includes(
                          task.status
                        )
                    );
                  } else {
                    // For "all" tasks, include all tasks
                    filteredTasks = filteredTasks;
                  }

                  if (filteredTasks.length === 0) {
                    toast({
                      title: 'No Tasks',
                      description: 'No tasks found to generate PDF',
                      variant: 'destructive',
                    });
                    return;
                  }

                  setIsGeneratingPDF(true);
                  try {
                    const filterType =
                      selectedTaskFilter === 'all'
                        ? 'All Tasks'
                        : selectedTaskFilter === 'completed'
                          ? 'Completed Tasks'
                          : selectedTaskFilter === 'inProgress'
                            ? 'In Progress Tasks'
                            : 'Not Completed Tasks';

                    await generateProjectTasksPDF(filteredTasks, {
                      title: `${filterType} - ${selectedProjectForPendingTasks.name}`,
                      projectName: selectedProjectForPendingTasks.name,
                      filterType: filterType,
                    });

                    toast({
                      title: 'Success',
                      description: 'PDF downloaded successfully',
                    });
                  } catch (error) {
                    console.error('Error generating PDF:', error);
                    toast({
                      title: 'Error',
                      description: 'Failed to generate PDF. Please try again.',
                      variant: 'destructive',
                    });
                  } finally {
                    setIsGeneratingPDF(false);
                  }
                }}
                disabled={isGeneratingPDF || !selectedProjectForPendingTasks}
                className="flex items-center gap-2"
              >
                {isGeneratingPDF ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Download PDF
                  </>
                )}
              </Button>
            </div>
          </DialogHeader>

          {selectedProjectForPendingTasks && (
            <div className="space-y-4">
              {/* Filter buttons */}
              <div className="flex gap-2 pb-2 border-b">
                <Button
                  variant={selectedTaskFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedTaskFilter('all')}
                >
                  All
                </Button>
                <Button
                  variant={
                    selectedTaskFilter === 'inProgress' ? 'default' : 'outline'
                  }
                  size="sm"
                  onClick={() => setSelectedTaskFilter('inProgress')}
                >
                  In Progress
                </Button>
                <Button
                  variant={
                    selectedTaskFilter === 'completed' ? 'default' : 'outline'
                  }
                  size="sm"
                  onClick={() => setSelectedTaskFilter('completed')}
                >
                  Completed
                </Button>
                <Button
                  variant={
                    selectedTaskFilter === 'notCompleted'
                      ? 'default'
                      : 'outline'
                  }
                  size="sm"
                  onClick={() => setSelectedTaskFilter('notCompleted')}
                >
                  Not Completed
                </Button>
              </div>

              {(() => {
                let filteredTasks = allTasks.filter(
                  task => task.projectId === selectedProjectForPendingTasks.id
                );

                if (selectedTaskFilter === 'completed') {
                  filteredTasks = filteredTasks.filter(
                    task => task.status === 'complete' && task.approvedAt
                  );
                } else if (selectedTaskFilter === 'inProgress') {
                  filteredTasks = filteredTasks.filter(task =>
                    ['started', 'working'].includes(task.status)
                  );
                } else if (selectedTaskFilter === 'notCompleted') {
                  filteredTasks = filteredTasks.filter(
                    task =>
                      !['complete', 'pending_approval', 'dropped'].includes(
                        task.status
                      )
                  );
                }

                return filteredTasks.length > 0 ? (
                  filteredTasks.map(task => (
                    <Card key={task.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-lg mb-2">
                              {task.title}
                            </h4>
                            {task.description && (
                              <p className="text-gray-600 mb-3">
                                {task.description}
                              </p>
                            )}
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span>Priority: {task.priority || 0}</span>
                              <span>
                                Due:{' '}
                                {task.dueDate
                                  ? new Date(task.dueDate).toLocaleDateString()
                                  : 'No due date'}
                              </span>
                              <span>
                                Created:{' '}
                                {task.createdAt
                                  ? new Date(
                                      task.createdAt
                                    ).toLocaleDateString()
                                  : 'Unknown'}
                              </span>
                            </div>
                          </div>
                          <Badge
                            variant={
                              task.status === 'complete'
                                ? 'default'
                                : task.status === 'working'
                                  ? 'secondary'
                                  : 'outline'
                            }
                          >
                            {task.status.replace('_', ' ')}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    <Target className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>
                      {selectedTaskFilter === 'all' &&
                        'No tasks found for this project.'}
                      {selectedTaskFilter === 'completed' &&
                        'No completed tasks found for this project.'}
                      {selectedTaskFilter === 'inProgress' &&
                        'No in progress tasks found for this project.'}
                      {selectedTaskFilter === 'notCompleted' &&
                        'No pending tasks found for this project.'}
                    </p>
                  </div>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
